import { PrimaryButton } from "@components/Buttons";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import Spin from "@components/UI/Spin";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { PermissionContext } from "../../contexts/PermissionContext";
import { useClarityVerificationRequest } from "../../hooks/useClarityVerificationRequest";
import { useTheme } from "@hooks/useTheme";
import { useUploadFileWithGit } from "../../hooks/useUploadFileWithGit";
import CalendarIcon from "../Icons/CalendarIcon";
import CheckedIcon from "../Icons/CheckedIcon";
import ClarityVerificationRequest from "../Project/ClarityVerificationRequest";
import { ConfirmProcessingModal, ConfirmActionModal  } from "./index";
import DotLoader from "react-spinners/DotLoader";
import { createWorkflowTestId, workflowStepSlug } from "@utils/workflowTestId";
import { CustomDatePicker } from "@components/Forms";

const WorkflowProgressClarity = ({
  steps = [],
  currentStep = 0,
  className = "",
  projectManagerId,
  onRejectVerification,
  onDeCompleteVerification,
  onMoveToUsageTypeCheck,
  onMoveToUsageTypeInput,
  onMoveToReview,
  onMoveToFollowUp,
  onMoveToFollowCheckUp,
  onRequestReVerification,
  onReUploadFile,
  onRejectFollowUp,
  onCompleteFollowUp,
  onDeCompleteFollowUp,
  onCompleteCheckFollowUp,
  onMoveToDone,
  onRejectReview,
  onRejectCheckFollowUp,
  onRejectRequestVerification,
  onRejectUsageTypeCheck,
  onRejectUsageTypeInput,
  forceCompleteText = false,
  isSkipProgress = "N",
  isLoading = false,
  isFetching = false,
  dataTestId = "workflow-progress-clarity",
}) => {
  const { colors } = useTheme();
  const tid = createWorkflowTestId(dataTestId);
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectSeq } = useParams();

  const canManageWorkflow = canManageProjectWorkflow(projectManagerId);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "",
    confirmButtonVariant: "primary",
    onConfirm: null,
  });
  const [isCloneRepositoryPending, setIsCloneRepositoryPending] = useState(false);

  /* Loading state for workflow buttons */

  // Request Usage Type Review
  const [isUsageTypeCheckLoading, setIsUsageTypeCheckLoading] = useState(false);
  // Usage Type Review
  const [isUsageTypeInputLoading, setIsUsageTypeInputLoading] = useState(false);
  const [isRejectUsageTypeCheckLoading, setIsRejectUsageTypeCheckLoading] = useState(false);
  // Usage Type Input
  const [isRejectUsageTypeInputLoading, setIsRejectUsageTypeInputLoading] = useState(false);
  const [isMoveToReviewLoading, setIsMoveToReviewLoading] = useState(false);
  // Review
  const [isRejectReviewLoading, setIsRejectReviewLoading] = useState(false);
  const [isRequestReVerificationLoading, setIsRequestReVerificationLoading] = useState(false);
  const [isMoveToFollowUpLoading, setIsMoveToFollowUpLoading] = useState(false);
  const [isMoveToDoneLoading, setIsMoveToDoneLoading] = useState(false);
  // Follow-up
  const [isRejectFollowUpLoading, setIsRejectFollowUpLoading] = useState(false);
  const [isCompleteFollowUpLoading, setIsCompleteFollowUpLoading] = useState(false);
  // Check-follow-up
  const [isRejectCheckFollowUpLoading, setIsRejectCheckFollowUpLoading] = useState(false);
  const [isCompleteCheckFollowUpLoading, setIsCompleteCheckFollowUpLoading] = useState(false);
  // Ready
  const [isDeCompleteVerificationLoading, setIsDeCompleteVerificationLoading] = useState(false);


  // Re-verification modal state with datetime picker
  const [reVerificationModal, setReVerificationModal] = useState({
    isOpen: false,
    selectedDateTime: new Date(),
  });

  // Clarity verification request modal (re-upload)
  const [clarityModal, setClarityModal] = useState({ isOpen: false });

  // Duplicate checksum confirmation modal
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    pendingUpload: null,
    pendingReuseCallback: null,
    pendingNewScanCallback: null,
  });

  // Add state for processing/loading
  const [processingRowId, setProcessingRowId] = useState(null);
  const [isReUploadVerificationPending, setIsReUploadVerificationPending] = useState(false);

  // Hook for checksum verification API
  const { handleReUploadVerification, isDuplicateCheckPending } = useClarityVerificationRequest();
  const { cloneRepositoryViaPta, cloneRepositoryViaSsh } = useUploadFileWithGit();

  const formatModalTitle = (title) => (typeof title === "string" ? title.toUpperCase() : title);

  const showConfirmation = (title, description, confirmText, confirmButtonVariant, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title: formatModalTitle(title),
      description,
      confirmText,
      confirmButtonVariant,
      onConfirm,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      description: "",
      confirmText: "",
      confirmButtonVariant: "primary",
      onConfirm: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "current";
    return "pending";
  };

  const getConnectorClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "h-1 flex-1 transition-all duration-200";
    if (status === "completed") {
      return `${baseClasses} ${colors.bgStatusSuccess}`;
    }
    return `${baseClasses} ${colors.bgStatusMuted}`;
  };

  const shouldShowCheckedIcon = (stepIndex, step) => {
    const status = getStepStatus(stepIndex);
    return status === "completed" || (status === "current" && step.label === "Ready");
  };

  // Re-verification handlers
  const handleRequestReVerificationClick = () => {
    // If we are in re-verification stage (special step 0), show simple confirmation
    if (currentStep === 0) {
      showConfirmation(
        t("common.re-verification-title"),
        t("common.re-verification-confirm"),
        t("common.re-verification-title"),
        "warning",
        () => onRequestReVerification && onRequestReVerification(null),
      );
    } else {
      // Otherwise open datetime picker modal
      setReVerificationModal({ isOpen: true, selectedDateTime: new Date() });
    }
  };

  const closeReVerificationModal = () => {
    setReVerificationModal({ isOpen: false, selectedDateTime: new Date() });
  };

  const handleConfirmRequestReVerification = async (selectedDate) => {
    if (isRequestReVerificationLoading) return;

    setIsRequestReVerificationLoading(true);
    try {
      if (onRequestReVerification) {
        await onRequestReVerification(selectedDate);
      }
    } catch (error) {
      console.error("Error in onRequestReVerification:", error);
    } finally {
      setIsRequestReVerificationLoading(false);
      closeReVerificationModal();
    }
  };

  const handleClarityModalOpen = () => {
    if (processingRowId !== null) return; // Prevent opening modal while processing
    setClarityModal({ isOpen: true });
  };
  const handleClarityModalClose = () => {
    if (processingRowId !== null) return; // Prevent closing modal while processing
    setClarityModal({ isOpen: false });
  };

  // Duplicate checksum modal handlers
  const handleDuplicateModalClose = () => setDuplicateModal({ 
    isOpen: false, 
    pendingUpload: null,
    pendingReuseCallback: null,
    pendingNewScanCallback: null,
  });
  
  const handleDuplicateConfirm = () => {
    if (duplicateModal.pendingUpload) duplicateModal.pendingUpload();
    handleDuplicateModalClose();
    // Navigate to project detail page after duplicate confirmation
    navigate(`/projects/${projectSeq}`, {
      state: {
        activeTab: "clarity",
        refreshData: true,
      },
    });
  };

  const handleReuseScan = () => {
    if (duplicateModal.pendingReuseCallback) {
      duplicateModal.pendingReuseCallback();
    }
    handleDuplicateModalClose();
    // Navigate to project detail page after reuse confirmation
    navigate(`/projects/${projectSeq}`, {
      state: {
        activeTab: "clarity",
        refreshData: true,
      },
    });
  };

  const handleNewScan = () => {
    if (duplicateModal.pendingNewScanCallback) {
      duplicateModal.pendingNewScanCallback();
    }
    handleDuplicateModalClose();
    // Navigate to project detail page after new scan confirmation
    navigate(`/projects/${projectSeq}`, {
      state: {
        activeTab: "clarity",
        refreshData: true,
      },
    });
  };

  const handleClarityModalSubmit = async (data) => {
    const { requestComment, accessToken, importMethod, verifFile, repositoryType, sshUrl, url, scanTypeCd } = data;

    if (importMethod === "repository") {
      try {
        if (accessToken) {
          const repoInfo = {
            url,
            token: accessToken,
          };
          setIsCloneRepositoryPending(true);
          await cloneRepositoryViaPta({ repoInfo, requestData: data });
          setIsCloneRepositoryPending(false);
        } else {
          const repoInfo = {
            url: sshUrl,
          };
          setIsCloneRepositoryPending(true);
          await cloneRepositoryViaSsh({ repoInfo, requestData: data });
          setIsCloneRepositoryPending(false);
        }
      } catch (error) {
        setIsCloneRepositoryPending(false);
        console.error("Repository cloning failed:", error);
        return; // Exit early if repository cloning fails
      }
    }

    // Handle duplicate check and processing
    const onDuplicateFound = (onReuse, onNewScan) => {
      // Support both old single callback and new dual callback patterns
      if (onNewScan) {
        // New pattern: two callbacks
        setDuplicateModal({
          isOpen: true,
          pendingUpload: null,
          pendingReuseCallback: onReuse,
          pendingNewScanCallback: onNewScan,
        });
      } else {
        // Legacy pattern: single callback (for backward compatibility)
        setDuplicateModal({
          isOpen: true,
          pendingUpload: onReuse,
          pendingReuseCallback: null,
          pendingNewScanCallback: null,
        });
      }
    };

    try {
      // Use the new re-upload functionality that creates new verification records
      setIsReUploadVerificationPending(true);
      await handleReUploadVerification(
        {
          projectSeq,
          projectId: data.projectId || "unknown",
          requestComment: requestComment || "",
          verifFile: data.verifFile,
          scanTypeCd: scanTypeCd || "CLARITY",
          repositoryImportFlag: data.repositoryImportFlag || "N",
        },
        onDuplicateFound,
        () => {
          // Navigate to project detail page after successful re-upload
          navigate(`/projects/${projectSeq}`, {
            state: {
              activeTab: "clarity",
              refreshData: true,
            },
          });
        },
        setProcessingRowId,
      );

      handleClarityModalClose();
    } catch (e) {
      console.error("Error in handleClarityModalSubmit:", e);
      // In case of error, still allow user to proceed with upload
      if (onReUploadFile) onReUploadFile(data);
      handleClarityModalClose();
    } finally {
      setIsReUploadVerificationPending(false);
    }
  };

  const renderStepButtons = () => {
    const currentStepData = steps[currentStep];
    if (!currentStepData) return null;

    const stepLabel = currentStepData.label;
    const stepSlug = workflowStepSlug(stepLabel);
    const stid = (name) => tid(`step-${stepSlug}-${name}`);

    switch (stepLabel) {
      case "Request ReVerification":
        return (
          <div className="flex justify-end items-center mt-5" data-testid={stid("actions")}>
            <div className="flex gap-2">
              <button
                data-testid={stid("re-upload-button")}
                onClick={handleClarityModalOpen}
                disabled={processingRowId !== null}
                className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${processingRowId ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {t("common.re-upload-button")}
              </button>
            </div>
          </div>
        );

      case "Analysis":
        return null;

      case "Usage type review": //Current step: Request Usage Type Review
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2"></div>
            <div className="flex gap-2">
              <PrimaryButton
                data-testid={stid("usage-type-check-request-button")}
                size="lg"
                disabled={isUsageTypeCheckLoading}
                onClick={() => {
                  showConfirmation(
                    t("common.workflow.usage-type-check-req"),
                    t("common.workflow.usage-type-check-confirm"),
                    t("common.workflow.request-check"),
                    "primary",
                    async () => {
                      setIsUsageTypeCheckLoading(true);
                      try {
                        if (onMoveToUsageTypeCheck) {
                          await onMoveToUsageTypeCheck();
                        }
                      } catch (error) {
                        console.error("Error in onMoveToUsageTypeCheck:", error);
                      } finally {
                        setIsUsageTypeCheckLoading(false);
                      }
                    },
                  );
                }}
              >
                {isUsageTypeCheckLoading && <Spin className="mr-2" />}
                {t("common.workflow.usage-type-check-req")}
              </PrimaryButton>
            </div>
          </div>
        );

      case "Usage type check": //Current step: Usage Type Review
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("reject-button")}
                  disabled={isUsageTypeInputLoading || isRejectUsageTypeCheckLoading}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isUsageTypeInputLoading || isRejectUsageTypeCheckLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.reject-usage-type-check"),
                      t("common.workflow.reject-usage-type-check-confirm"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectUsageTypeCheckLoading(true);
                        try {
                          if (onRejectUsageTypeCheck) {
                            await onRejectUsageTypeCheck();
                          }
                        } catch (error) {
                          console.error("Error in onRejectUsageTypeCheck:", error);
                        } finally {
                          setIsRejectUsageTypeCheckLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isRejectUsageTypeCheckLoading && <Spin className="mr-2" />}
                  {t("common.workflow.reject")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <PrimaryButton
                  data-testid={stid("usage-type-input-request-button")}
                  size="lg"
                  disabled={isUsageTypeInputLoading || isRejectUsageTypeCheckLoading}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.usage-type-input-req-title-modal"),
                      t("common.workflow.usage-type-input-confirm"),
                      t("common.workflow.request-input"),
                      "primary",
                      async () => {
                        setIsUsageTypeInputLoading(true);
                        try {
                          if (onMoveToUsageTypeInput) {
                            await onMoveToUsageTypeInput();
                          }
                        } catch (error) {
                          console.error("Error in onMoveToUsageTypeInput:", error);
                        } finally {
                          setIsUsageTypeInputLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isUsageTypeInputLoading && <Spin className="mr-2" />}
                  {t("common.workflow.usage-type-input-req")}
                </PrimaryButton>
              )}
            </div>
          </div>
        );

      case "Usage type input":
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2">
            {canManageWorkflow && (
                <button
                  data-testid={stid("reject-button")}
                  disabled={isRejectUsageTypeInputLoading || isMoveToReviewLoading}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isRejectUsageTypeInputLoading || isMoveToReviewLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.reject-usage-type-input"),
                      t("common.workflow.reject-usage-type-input-confirm"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectUsageTypeInputLoading(true);
                        try {
                          if (onRejectUsageTypeInput) {
                            await onRejectUsageTypeInput();
                          }
                        } catch (error) {
                          console.error("Error in onRejectUsageTypeInput:", error);
                        } finally {
                          setIsRejectUsageTypeInputLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isRejectUsageTypeInputLoading && <Spin className="mr-2" />}
                  {t("common.workflow.reject")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <PrimaryButton
                data-testid={stid("request-review-button")}
                size="lg"
                disabled={isRejectUsageTypeInputLoading || isMoveToReviewLoading}
                onClick={() => {
                  showConfirmation(
                    t("common.workflow.request-review"),
                    t("common.workflow.request-review-confirm"),
                    t("common.ok-button"),
                    "primary",
                    async () => {
                      setIsMoveToReviewLoading(true);
                      try {
                        if (onMoveToReview) {
                          await onMoveToReview();
                        }
                      } catch (error) {
                        console.error("Error in onMoveToReview:", error);
                      } finally {
                        setIsMoveToReviewLoading(false);
                      }
                    },
                  );
                }}
              >
                {isMoveToReviewLoading && <Spin className="mr-2" />}
                {t("common.workflow.request-review")}
              </PrimaryButton>
            </div>
          </div>
        );

      case "Review":
        const isReviewActionLoading = isRejectReviewLoading || isRequestReVerificationLoading || isMoveToFollowUpLoading || isMoveToDoneLoading;
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            {canManageWorkflow && (
              <>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("reject-button")}
                    disabled={isReviewActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.workflow.reject-review"),
                        t("common.workflow.reject-review-confirm"),
                        t("common.workflow.reject"),
                        "danger",
                        async () => {
                          setIsRejectReviewLoading(true);
                          try {
                            if (onRejectReview) {
                              await onRejectReview();
                            }
                          } catch (error) {
                            console.error("Error in onRejectReview:", error);
                          } finally {
                            setIsRejectReviewLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRejectReviewLoading && <Spin className="mr-2" />}
                    {t("common.workflow.reject")}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("request-re-verification-button")}
                    disabled={isReviewActionLoading}
                    onClick={handleRequestReVerificationClick}
                    className={`min-w-28 min-h-10 rounded-lg border px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80 ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRequestReVerificationLoading && <Spin className="mr-2" />}
                    {t("common.workflow.request-re-verif")}
                  </button>
                  <button
                    data-testid={stid("order-follow-up-button")}
                    disabled={isReviewActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.workflow.order-follow-up"),
                        t("common.workflow.order-follow-up-confirm"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsMoveToFollowUpLoading(true);
                          try {
                            if (onMoveToFollowUp) {
                              await onMoveToFollowUp();
                            }
                          } catch (error) {
                            console.error("Error in onMoveToFollowUp:", error);
                          } finally {
                            setIsMoveToFollowUpLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isMoveToFollowUpLoading && <Spin className="mr-2" />}
                    {t("common.workflow.order-follow-up")}
                  </button>
                  <PrimaryButton
                    data-testid={stid("force-complete-verification-button")}
                    size="lg"
                    disabled={isReviewActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.workflow.force-complete-verification"),
                        t("common.workflow.force-complete-verification-confirm"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsMoveToDoneLoading(true);
                          try {
                            if (onMoveToDone) {
                              await onMoveToDone();
                            }
                          } catch (error) {
                            console.error("Error in onMoveToDone:", error);
                          } finally {
                            setIsMoveToDoneLoading(false);
                          }
                        },
                      );
                    }}
                  >
                    {isMoveToDoneLoading && <Spin className="mr-2" />}
                    {t("common.workflow.force-complete-verification")}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        );

      case "Follow-up":
        const isFollowUpActionLoading = isRejectFollowUpLoading || isRequestReVerificationLoading || isCompleteFollowUpLoading || isMoveToDoneLoading;
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("reject-button")}
                  disabled={isFollowUpActionLoading}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.reject-follow-up"),
                      t("common.workflow.reject-follow-up-confirm"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectFollowUpLoading(true);
                        try {
                          if (onRejectFollowUp) {
                            await onRejectFollowUp();
                          }
                        } catch (error) {
                          console.error("Error in onRejectFollowUp:", error);
                        } finally {
                          setIsRejectFollowUpLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isRejectFollowUpLoading && <Spin className="mr-2" />}
                  {t("common.workflow.reject")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("request-re-verification-button")}
                  disabled={isFollowUpActionLoading}
                  onClick={handleRequestReVerificationClick}
                  className={`min-w-28 min-h-10 rounded-lg border px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80 ${isFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isRequestReVerificationLoading && <Spin className="mr-2" />}
                  {t("common.workflow.request-re-verif")}
                </button>
              )}
              <PrimaryButton
                data-testid={stid("complete-follow-up-button")}
                size="lg"
                disabled={isFollowUpActionLoading}
                onClick={() => {
                  showConfirmation(
                    t("common.workflow.complete-follow-up"),
                    t("common.workflow.complete-follow-up-confirm"),
                    t("common.confirm"),
                    "primary",
                    async () => {
                      setIsCompleteFollowUpLoading(true);
                      try {
                        if (onMoveToFollowCheckUp) {
                          await onMoveToFollowCheckUp();
                        }
                      } catch (error) {
                        console.error("Error in onMoveToFollowCheckUp:", error);
                      } finally {
                        setIsCompleteFollowUpLoading(false);
                      }
                    },
                  );
                }}
              >
                {isCompleteFollowUpLoading && <Spin className="mr-2" />}
                {t("common.workflow.complete-follow-up")}
              </PrimaryButton>
              {canManageWorkflow && (
                <PrimaryButton
                  data-testid={stid("force-complete-verification-button")}
                  size="lg"
                  disabled={isFollowUpActionLoading}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.force-complete-verification"),
                      t("common.workflow.force-complete-verification-confirm"),
                      t("common.confirm"),
                      "primary",
                      async () => {
                        setIsMoveToDoneLoading(true);
                        try {
                          if (onMoveToDone) {
                            await onMoveToDone();
                          }
                        } catch (error) {
                          console.error("Error in onMoveToDone:", error);
                        } finally {
                          setIsMoveToDoneLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isMoveToDoneLoading && <Spin className="mr-2" />}
                  {t("common.workflow.force-complete-verification")}
                </PrimaryButton>
              )}
            </div>
          </div>
        );

      case "Check follow-up":
        const isCheckFollowUpActionLoading = isRejectCheckFollowUpLoading || isRequestReVerificationLoading || isCompleteCheckFollowUpLoading;
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            {canManageWorkflow && (
              <>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("reject-button")}
                    disabled={isCheckFollowUpActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.workflow.reject-check-follow-up"),
                        t("common.workflow.reject-check-follow-up-confirm"),
                        t("common.workflow.reject"),
                        "danger",
                        async () => {
                          setIsRejectCheckFollowUpLoading(true);
                          try {
                            if (onRejectCheckFollowUp) {
                              await onRejectCheckFollowUp();
                            }
                          } catch (error) {
                            console.error("Error in onRejectCheckFollowUp:", error);
                          } finally {
                            setIsRejectCheckFollowUpLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isCheckFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRejectCheckFollowUpLoading && <Spin className="mr-2" />}
                    {t("common.workflow.reject")}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("request-re-verification-button")}
                    disabled={isCheckFollowUpActionLoading}
                    onClick={handleRequestReVerificationClick}
                    className={`min-w-28 min-h-10 rounded-lg border px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80 ${isCheckFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRequestReVerificationLoading && <Spin className="mr-2" />}
                    {t("common.workflow.request-re-verif")}
                  </button>
                  <PrimaryButton
                    data-testid={stid("complete-verification-button")}
                    size="lg"
                    disabled={isCheckFollowUpActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.workflow.complete-verification"),
                        t("common.workflow.complete-verification-confirm"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsCompleteCheckFollowUpLoading(true);
                          try {
                            if (onCompleteCheckFollowUp) {
                              await onCompleteCheckFollowUp();
                            }
                          } catch (error) {
                            console.error("Error in onCompleteCheckFollowUp:", error);
                          } finally {
                            setIsCompleteCheckFollowUpLoading(false);
                          }
                        },
                      );
                    }}
                  >
                    {isCompleteCheckFollowUpLoading && <Spin className="mr-2" />}
                    {forceCompleteText
                      ? t("common.workflow.force-complete-verification")
                      : t("common.workflow.complete-verification")}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        );

      case "Ready":
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && isSkipProgress !== "Y" && (
                <button
                  data-testid={stid("decomplete-verification-button")}
                  disabled={isDeCompleteVerificationLoading}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.decomplete-verification"),
                      t("common.workflow.decomplete-verification-confirm"),
                      t("common.workflow.decomplete-verification"),
                      "danger",
                      async () => {
                        setIsDeCompleteVerificationLoading(true);
                        try {
                          if (onDeCompleteVerification) {
                            await onDeCompleteVerification();
                          }
                        } catch (error) {
                          console.error("Error in onDeCompleteVerification:", error);
                        } finally {
                          setIsDeCompleteVerificationLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isDeCompleteVerificationLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isDeCompleteVerificationLoading && <Spin className="mr-2" />}
                  {t("common.workflow.decomplete-verif")}
                </button>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="flex justify-between items-center mt-16">
            <div className="flex gap-2"></div>
          </div>
        );
    }
  };

  const isAnyLoading = isLoading || isFetching;

  return (
    <div className={`w-full h-40 relative ${className}`} data-testid={tid("root")}>
      {isAnyLoading && (
        <div
          data-testid={tid("loading-overlay")}
          className={`absolute inset-0 z-50 flex flex-col items-center justify-center ${colors.bgPrimary} rounded-lg`}
        >
          <DotLoader
            color={colors.textActiveColor}
            loading={isAnyLoading}
            size={60}
            speedMultiplier={1.2}
            aria-label="Loading"
          />
        </div>
      )}
      {currentStep === 0 && steps[0]?.label === "Request ReVerification" ? (
        <>
          <div className="flex justify-center items-center px-8 mt-4 mb-12" data-testid={tid("re-verification-step")}>
            <div className="relative flex flex-col items-center">
              <div>
                <div className={`w-14 h-14 ${colors.textSecondary} flex items-center justify-center opacity-60`}>
                  <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Git-like branching icon with three circles connected by lines */}
                    <circle cx="16" cy="16" r="5" fill="currentColor" />
                    <circle cx="16" cy="48" r="5" fill="currentColor" />
                    <circle cx="48" cy="48" r="5" fill="currentColor" />
                    <path
                      d="M16 21 L16 43 M16 43 L48 43"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
              <div
                className={`absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2 text-center text-sm font-semibold whitespace-nowrap px-3 py-1 border-2 border-dashed rounded-lg ${colors.borderActiveDashed} ${colors.textActive} bg-opacity-10 ${colors.bgButton}`}
              >
                {t("status.re-verification-request")}
              </div>
            </div>
          </div>
          {renderStepButtons()}
        </>
      ) : (
        <>
          <div className="flex justify-center items-center px-8 mt-4 mb-20" data-testid={tid("steps-track")}>
            {steps.slice(1).map((step, index) => (
              <React.Fragment key={step.label || index}>
                <div
                  className="relative flex flex-col items-center"
                  data-testid={tid(`step-${workflowStepSlug(step.label)}`)}
                >
                  <div>
                    {shouldShowCheckedIcon(index + 1, step) ? (
                      <CheckedIcon size={56} />
                    ) : (
                      step.icon && <step.icon size={56} isActive={getStepStatus(index + 1) === "current"} />
                    )}
                  </div>
                  <div
                    className={`absolute top-16 left-1/2 -translate-x-1/2 mt-2 min-w-30 max-[1600px]:max-w-[120px] text-center text-sm font-semibold whitespace-nowrap max-[1600px]:whitespace-normal max-[1600px]:break-words ${getStepStatus(index + 1) === "current" && step.label !== "Ready" ? colors.textStatusActiveStep : colors.textPrimary}`}
                  >
                    {step.displayLabel}
                  </div>
                </div>
                {index < steps.slice(1).length - 1 && (
                  <div className={`${getConnectorClasses(index + 1)} max-w-40`} key={`connector-${index}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          {renderStepButtons()}
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmActionModal
        data-testid={tid("confirmation-modal")}
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText={confirmationModal.confirmText}
        confirmButtonVariant={confirmationModal.confirmButtonVariant}
      />

      {/* Re-Verification Modal with DateTime Picker */}
      {reVerificationModal.isOpen && (
        <div
          data-testid={tid("re-verification-modal-overlay")}
          className={`fixed inset-0 z-[9998] flex items-center justify-start md:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
          onClick={closeReVerificationModal}
        >
          <div
            data-testid={tid("re-verification-modal")}
            className={`relative w-[448px] min-w-[448px] ${colors.bgModal} transform rounded-lg shadow-xl transition-all duration-200`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="mb-4 flex items-center justify-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center">
                  <CalendarIcon size={48} />
                </div>
              </div>
              <div className="mb-4 text-center">
                <h3 className={`text-lg font-semibold uppercase ${colors.textPrimary} mb-2`}>
                  {t("common.re-verification-title")}
                </h3>
                <p className={`text-sm ${colors.textSecondary} mb-4`}>{t("common.re-verification-with-date")}</p>
                <div className="mb-4">
                  <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                    {t("common.due-date-time")}
                  </label>
                  <CustomDatePicker
                    data-testid={tid("re-verification-datetime-picker")}
                    selected={reVerificationModal.selectedDateTime}
                    onChange={(newDate) => setReVerificationModal((prev) => ({ ...prev, selectedDateTime: newDate }))}
                    minDate={new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())}
                    dateFormat="yyyy-MM-dd"
                    className="w-full"
                  />
                </div>
              </div>
              <div className="mt-6 flex gap-3 flex-row justify-center">
                <button
                  data-testid={tid("re-verification-cancel-button")}
                  onClick={closeReVerificationModal}
                  className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${colors.borderPrimary} ${colors.textSecondary} ${colors.bgSecondary} min-w-[100px] ${colors.focusRing2} focus:outline-none`}
                >
                  {t("common.cancel-button")}
                </button>
                <PrimaryButton
                  data-testid={tid("re-verification-confirm-button")}
                  size="lg"
                  disabled={isRequestReVerificationLoading}
                  onClick={() => handleConfirmRequestReVerification(reVerificationModal.selectedDateTime)}
                >
                  {isRequestReVerificationLoading && <Spin className="mr-2" />}
                  {isRequestReVerificationLoading ? t("update_messages.PROCESSING") : t("common.confirm")}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clarity Verification Request (Re-Upload) Modal */}
      <ClarityVerificationRequest
        isOpen={clarityModal.isOpen}
        onClose={handleClarityModalClose}
        onSubmit={handleClarityModalSubmit}
        setHasErrors={() => {}}
        setRequestData={() => {}}
        setRepoInfo={() => {}}
      />

      {/* Duplicate Modal */}
      {duplicateModal.isOpen && (
        <ConfirmProcessingModal
          data-testid={tid("duplicate-file-modal")}
          isOpen={duplicateModal.isOpen}
          onClose={handleDuplicateModalClose}
          onConfirm={handleDuplicateConfirm}
          onReuseScan={duplicateModal.pendingReuseCallback ? handleReuseScan : undefined}
          onNewScan={duplicateModal.pendingNewScanCallback ? handleNewScan : undefined}
          title={formatModalTitle(t("project.duplicate-file-warning"))}
          message={duplicateModal.pendingReuseCallback && duplicateModal.pendingNewScanCallback ? t("project.duplicate-file-warning-message-new") : t("project.duplicate-file-warning-description")}
          confirmText={t("common.yes")}
          cancelText={t("common.cancel-button")}
          reuseText={t("project.reuse-previous-scan-results")}
          newScanText={t("project.perform-new-scan")}
        />
      )}
      <FullScreenDotLoader
        loading={
          isDuplicateCheckPending ||
          isCloneRepositoryPending ||
          isReUploadVerificationPending ||
          processingRowId !== null
        }
        text={
          isDuplicateCheckPending
            ? t("project.verification-request.validating-file") || "Validating file..."
            : isCloneRepositoryPending
              ? t("project.verification-request.cloning-repository") || "Cloning repository..."
              : isReUploadVerificationPending
                ? t("project.verification-request.upload-verification-file") || "Uploading verification file..."
                : t("project.verification-request.upload-verification-file") || "Uploading verification file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
};

export default WorkflowProgressClarity;
